import { STAFF_PATHS } from '../utils/staffPaths';
import {
  TbBuildingSkyscraper, TbChartLine, TbUsersGroup, TbPackages, TbRocket, TbTemplate,
  TbAdjustments, TbUserShield, TbListDetails, TbBulb, TbRobot, TbCreditCard,
  TbAddressBook, TbBrandWindows, TbFingerprint, TbMail,
} from 'react-icons/tb';

// The staff portal's navigation, in one place: the rail, the drawer, the breadcrumb and
// each page's heading all read from here, so a screen cannot be named one thing in the
// rail and another in its title.
//
// Every administration section has its own address (/admin/dashboard/<slug>), the same
// way every customer screen does, so it can be bookmarked, refreshed and reached with
// Back. `admin` marks what the Marketing role must not see.
export const STAFF_GROUPS = [
  {
    key: 'workspace',
    label: 'Workspace',
    items: [
      { key: 'tenants', label: 'Tenants', icon: TbBuildingSkyscraper, admin: true,
        desc: 'Every MSP workspace, its plan, its users and its usage.' },
      { key: 'marketing', label: 'Marketing', icon: TbChartLine, path: STAFF_PATHS.marketing,
        desc: 'See how MSP workspaces start trials, activate, and renew. Every account metric counts tenants, not individual users.' },
      { key: 'team', label: 'Staff team', icon: TbUsersGroup, path: STAFF_PATHS.team, admin: true,
        desc: 'Give your company’s Marketing team access to MSP growth metrics.' },
    ],
  },
  {
    key: 'platform',
    label: 'Platform',
    admin: true,
    items: [
      { key: 'plans', label: 'Plans & features', icon: TbPackages,
        desc: 'Decide what each plan includes — scanner modules and platform features alike.' },
      { key: 'releases', label: 'Scanner releases', icon: TbRocket,
        desc: 'Publish new scanner versions and manage what tenants download.' },
      { key: 'templates', label: 'System templates', icon: TbTemplate,
        desc: 'Report templates every workspace starts from.' },
      { key: 'thresholds', label: 'Category thresholds', icon: TbAdjustments,
        desc: 'The default scoring bands behind every report.' },
      { key: 'users', label: 'Users & groups', icon: TbUserShield,
        desc: 'Tenant users, their roles and their access.' },
    ],
  },
  {
    key: 'operations',
    label: 'Operations',
    admin: true,
    items: [
      { key: 'logs', label: 'Logs', icon: TbListDetails,
        desc: 'Platform activity across every service.' },
      { key: 'access-policy', label: 'Admin access', icon: TbUserShield,
        desc: 'Manage the shared company VPN policy for internal staff.' },
      { key: 'feature-requests', label: 'Feature requests', icon: TbBulb,
        desc: 'What tenants have asked for, and how it is progressing.' },
    ],
  },
  {
    key: 'integrations',
    label: 'Integrations',
    admin: true,
    items: [
      { key: 'ai', label: 'AI', icon: TbRobot, desc: 'Platform-level OpenAI configuration for reusable report generation.' },
      { key: 'billing', label: 'Stripe billing', icon: TbCreditCard, desc: 'The Pro trial new workspaces start with, and native Pipsqueak Pro payments.' },
      { key: 'gohighlevel', label: 'GoHighLevel', icon: TbAddressBook, desc: 'Sync every tenant into GoHighLevel as a contact, tagged by app tier (Community vs Pro). The same contact is updated on upgrade — no duplicates.' },
      { key: 'microsoft', label: 'Microsoft sign-in', icon: TbBrandWindows, desc: 'Let users sign in with their Microsoft (Entra ID) work account. Existing users are matched by email; new users are created when their email domain already belongs to a registered tenant.' },
      { key: 'passkeys', label: 'Passkeys', icon: TbFingerprint, desc: 'Passwordless sign-in settings.' },
      { key: 'email', label: 'Email (SMTP)', icon: TbMail, desc: 'Outbound sign-in and verification email. Configure the mailbox here instead of a server .env file.' },
    ],
  },
];

// Settings is reached from the account panel, not the rail, as it is in the customer shell.
export const STAFF_SETTINGS = {
  key: 'settings', label: 'Account settings', path: STAFF_PATHS.settings,
  desc: 'Manage your staff password and sign-in security.',
};

export const DEFAULT_ADMIN_SECTION = 'tenants';

export const staffPath = (item) => item.path || `${STAFF_PATHS.dashboard}/${item.key}`;

export const staffGroupsFor = (role) => STAFF_GROUPS
  .filter((g) => !g.admin || role === 'admin')
  .map((g) => ({ ...g, items: g.items.filter((i) => !i.admin || role === 'admin') }))
  .filter((g) => g.items.length);

// The section a URL points at, with its group. Unknown slugs resolve to nothing, so the
// dashboard can fall back to its default rather than render an empty page.
export const findStaffItem = (key) => {
  if (key === STAFF_SETTINGS.key) return { item: STAFF_SETTINGS, group: null };
  for (const group of STAFF_GROUPS) {
    const item = group.items.find((i) => i.key === key);
    if (item) return { item, group };
  }
  return null;
};
